<script setup lang="ts">
import CloseIcon from 'assets/icons/close/24/close-mobile-modal.svg'
import useMainStore from '~/shared/store'
import AppButton from '~/components/ui/buttons/AppButton.vue';
import type { IMobileModalSettings } from '~/shared/interfaces/ui/modal';

const mainStore = useMainStore()
let startY = 0
let endY = 0

const props = defineProps<IMobileModalSettings>();

const emits = defineEmits(['close-modal', 'confirm', 'clear']);

watch(() => props.modalIsOpen, (modalIsOpen: boolean) => {
  mainStore.setBodyFixed(modalIsOpen)
}, {
  immediate: true
})

function closeModal() {
  emits('close-modal', true);
}

function handleSwipe(event: TouchEvent) {
  if (!props.fullPage) {
    if (event.type === 'touchstart') {
      startY = event.touches[0].clientY
    } else {
      endY = event.changedTouches[0].clientY
    }

    if (endY - startY > 60) {
      closeModal()
    }
  }
}

function clear() {
  emits('clear')
}
</script>

<template>
  <div
    class="mobile-modal"
    :class="[
      modalIsOpen && 'active',
      fullPage && 'isFullHeight',
      hasScroll && `mobile-modal_scroll`
    ]"
    @touchstart.passive="handleSwipe"
    @touchend="handleSwipe"
  >
    <div
      class="mobile-modal__content"
    >
      <div
        v-if="!fullPage"
        @click="closeModal"
        class="mobile-modal__line"
      ></div>

      <div
        class="mobile-modal__header"
        v-if="showHeader"
      >
        <CloseIcon
          class="mobile-modal__header-back"
          v-if="!hideClose"
          @click="closeModal"
        />
        <div class="mobile-modal__header-title">{{ headerTitle }}</div>
        <Transition name="fade">
          <div
            class="mobile-modal__header-clear"
            v-if="showHeaderClear"
            @click="clear"
          >
            Сбросить
          </div>
        </Transition>
      </div>
      <div
        class="mobile-modal__content-wrapper"
        :class="[
          showAction && 'mobile-modal__content-wrapper_has-action',
          showHeader && 'mobile-modal__content-wrapper_has-header',
          (showAction && showHeader) && 'mobile-modal__content-wrapper_has-all'
        ]"
      >
        <slot />
      </div>
      <div
        class="mobile-modal__action"
        v-if="showAction"
      >
        <div class="mobile-modal__action-linear"></div>
        <AppButton
          @click="emits('confirm')"
          class="mobile-modal__action-button"
          :text="actionText"
        />
      </div>
    </div>
    <div
      v-if="!fullPage"
      @click="closeModal"
      class="mobile-modal__backdrop"
    >

    </div>
  </div>
</template>

<style lang="scss" scoped>
.mobile-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  visibility: hidden;
  &__header {
    position: relative;
    margin-bottom: 8px;
    &-back, &-clear {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
    }
    &-clear {
      right: 16px;
      color: $control-content_orange;
      @include font(14px, 20px);
    }
    &-back {
      left: 0;
    }
    &-title {
      text-align: center;
      color: $content_primary;
      @include font(18px, 24px, 600);
    }
  }
  &__action {
    position: absolute;
    bottom: 12px;
    width: calc(100% - 40px);
    left: 20px;
    &-linear {
      height: 20px;
      background: linear-gradient(180deg, $control-bg_white 0%, rgba(255, 255, 255, 0) 0.01%, $control-bg_white 100%);
    }
    &-button {
      width: 100%;
    }
  }
  &_scroll {
    .mobile-modal__content {
      padding: 0 4px 0 20px;
    }
  }

  &.isFullHeight {
    padding: 20px;

    .mobile-modal__content {
      top: 0;
      bottom: auto;
      border-radius: 0;
      max-height: 100%;
      height: 100%;
      padding-top: 24px;
      opacity: 0;
    }
  }
  &.active.isFullHeight {
    .mobile-modal__content {
      bottom: auto;
      opacity: 1;
    }
  }
  &.active {
    visibility: visible;
    .mobile-modal__content {
      bottom: 0;
    }

    .mobile-modal__backdrop  {
      opacity: 1;
      visibility: visible;
    }
  }
  &__content {
    position: absolute;
    z-index: 1;
    width: 100%;
    right: 0;
    bottom: -100%;
    padding: 32px 20px 24px;
    background: $bg_page;
    border-radius: 12px 12px 0 0;
    transition: all 0.3s;
    &-wrapper {
      height: 100%;
      overflow: auto;
      &_has-action {
        height: calc(100% - 65px);
      }
      &_has-header {
        height: calc(100% - 24px);
      }
      &_has-all {
        height: calc(100% - 24px);
      }
    }
  }
  &__backdrop {
    position: absolute;
    opacity: 0;
    visibility: hidden;
    width: 100%;
    height: 100%;
    background: rgba(31, 31, 31, 0.50);
    transition: 0.3s all;
  }

  &__line {
    width: 40px;
    height: 4px;
    border-radius: 5px;
    background-color: $bg_border;
    position: absolute;
    top: 8px;
    left: 50%;
    transform: translateX(-50%);
  }
}
</style>
